import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyBankTransferAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchVerifyBankTransferQueue = createAsyncThunk(
    'verifyBankTransfer/fetchQueue',
    async (roleId, { rejectWithValue }) => {
        try { return await api.getVerificationBankTransfers(roleId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch bank transfer verification queue')); }
    }
);

export const fetchVerifyBankTransferDetail = createAsyncThunk(
    'verifyBankTransfer/fetchDetail',
    async (refNo, { rejectWithValue }) => {
        try { return await api.getVerificationBankTransferByRefNo(refNo); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch bank transfer details')); }
    }
);

export const fetchReturnedBankTransferDetail = createAsyncThunk(
    'verifyBankTransfer/fetchReturnedDetail',
    async (refNo, { rejectWithValue }) => {
        try { return await api.getReturnedBankTransferByRefNo(refNo); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch returned bank transfer details')); }
    }
);

export const fetchTransferBanks = createAsyncThunk(
    'verifyBankTransfer/fetchBanks',
    async (_, { rejectWithValue }) => {
        try { return await api.getBanks(); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch banks')); }
    }
);

export const submitVerifyBankTransfer = createAsyncThunk(
    'verifyBankTransfer/verify',
    async (payload, { rejectWithValue }) => {
        try { return await api.verifyBankTransfer(payload); }
        catch (e) { return rejectWithValue(err(e, 'Failed to submit bank transfer verification')); }
    }
);

export const submitUpdateBankTransfer = createAsyncThunk(
    'verifyBankTransfer/update',
    async (payload, { rejectWithValue }) => {
        try { return await api.updateReturnedBankTransfer(payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred While Return Bank Transfer Updation')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailLoading: false, detailError: null,
    banks: [], banksLoading: false,
    verifyLoading: false, updateLoading: false,
};

// The pending-detail endpoint returns a one-row list; the returned-detail endpoint a single object.
const firstRow = (data) => (Array.isArray(data) ? data[0] || null : data || null);

const verifyBankTransferSlice = createSlice({
    name: 'verifyBankTransfer',
    initialState,
    reducers: {
        clearVerifyBankTransferDetail: (s) => { s.detail = null; s.detailError = null; s.detailLoading = false; },
        resetVerifyBankTransfer: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVerifyBankTransferQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchVerifyBankTransferQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchVerifyBankTransferQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        [fetchVerifyBankTransferDetail, fetchReturnedBankTransferDetail].forEach((thunk) => {
            builder
                .addCase(thunk.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.detailLoading = false; s.detail = firstRow(a.payload?.Data); })
                .addCase(thunk.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });
        });

        builder
            .addCase(fetchTransferBanks.pending, (s) => { s.banksLoading = true; })
            .addCase(fetchTransferBanks.fulfilled, (s, a) => { s.banksLoading = false; s.banks = a.payload?.Data || []; })
            .addCase(fetchTransferBanks.rejected, (s) => { s.banksLoading = false; s.banks = []; });

        builder
            .addCase(submitVerifyBankTransfer.pending, (s) => { s.verifyLoading = true; })
            .addCase(submitVerifyBankTransfer.fulfilled, (s) => { s.verifyLoading = false; })
            .addCase(submitVerifyBankTransfer.rejected, (s) => { s.verifyLoading = false; });

        builder
            .addCase(submitUpdateBankTransfer.pending, (s) => { s.updateLoading = true; })
            .addCase(submitUpdateBankTransfer.fulfilled, (s) => { s.updateLoading = false; })
            .addCase(submitUpdateBankTransfer.rejected, (s) => { s.updateLoading = false; });
    },
});

export const { clearVerifyBankTransferDetail, resetVerifyBankTransfer } = verifyBankTransferSlice.actions;

export const selectVBTQueue = (s) => s.verifyBankTransfer.queue;
export const selectVBTQueueLoading = (s) => s.verifyBankTransfer.queueLoading;
export const selectVBTQueueError = (s) => s.verifyBankTransfer.queueError;
export const selectVBTDetail = (s) => s.verifyBankTransfer.detail;
export const selectVBTDetailLoading = (s) => s.verifyBankTransfer.detailLoading;
export const selectVBTDetailError = (s) => s.verifyBankTransfer.detailError;
export const selectVBTBanks = (s) => s.verifyBankTransfer.banks;
export const selectVBTVerifyLoading = (s) => s.verifyBankTransfer.verifyLoading;
export const selectVBTUpdateLoading = (s) => s.verifyBankTransfer.updateLoading;

export default verifyBankTransferSlice.reducer;
